import { useEffect, useRef, useState } from 'react'
import { useNodeLogs, usePersistedNodeLogs } from '@/hooks'
import { useParams } from 'react-router-dom'
import type { WorkflowRunNode } from '@/types'
import { Loader2, FileText } from 'lucide-react'
import { Button } from '@/components/ui'
import { CopyButton } from '@/components/ui/copy-button'
import { cn } from '@/lib/utils'

type LogFilter = 'all' | 'system' | 'errors'

function filterLogs(raw: string, filter: LogFilter): string {
  if (filter === 'all') return raw
  const prefix = filter === 'system' ? '[system]' : '[error]'
  return raw
    .split('\n')
    .filter(line => line.startsWith(prefix))
    .join('\n')
}

interface Props {
  node: WorkflowRunNode
}

const CONTAINER_OUTPUT_HEADER = '[container] --- Container Output ---'

function resolveDisplayedLogs(baseLogs: string, fullLogs: string | null): string | null {
  if (fullLogs === null) {
    return baseLogs || null
  }
  if (!baseLogs) {
    return fullLogs
  }
  const separator = baseLogs.includes(CONTAINER_OUTPUT_HEADER) ? '\n' : `\n\n${CONTAINER_OUTPUT_HEADER}\n`
  return `${baseLogs}${separator}${fullLogs}`
}

export function LogViewer({ node }: Props) {
  const { projectId, runId } = useParams()
  const isRunning = node.status === 'Running' || node.status === 'RetryPending'
  const containerRef = useRef<HTMLDivElement>(null)
  const shouldAutoScroll = useRef(true)
  const [loadPersisted, setLoadPersisted] = useState(false)
  const [logFilter, setLogFilter] = useState<LogFilter>('all')

  const { data, isLoading } = useNodeLogs(
    projectId!,
    runId!,
    node.id,
    node.status,
  )

  const persisted = usePersistedNodeLogs(projectId!, runId!, node.id, loadPersisted)

  const persistedLogs = persisted.data?.logs ?? null
  const liveContainerLogsAvailable = data?.liveContainerLogsAvailable ?? false
  const noPersistedLogs = loadPersisted && !persisted.isFetching && !persisted.isError && persistedLogs === null

  const lastLiveLogs = data?.lastLiveLogs ?? null
  const pausedLogs =
    isRunning && data && !liveContainerLogsAvailable && lastLiveLogs?.containerId === data.containerId ? lastLiveLogs.logs : null
  const liveLogsPaused = pausedLogs !== null

  const canLoadFullLogs =
    !isRunning && node.containerStrategy !== 'None' && !liveContainerLogsAvailable && !persistedLogs && !noPersistedLogs

  const baseLogs = pausedLogs ?? (data?.hasLogs ? data.logs ?? '' : '')
  const displayedLogs = resolveDisplayedLogs(baseLogs, persistedLogs)

  useEffect(() => {
    if (shouldAutoScroll.current && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [data?.logs, persistedLogs])

  const handleScroll = () => {
    if (!containerRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current
    shouldAutoScroll.current = scrollHeight - scrollTop - clientHeight < 100
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-log-viewer" role="status" aria-live="polite">
        <Loader2 className="h-5 w-5 animate-spin text-log-viewer-foreground" />
      </div>
    )
  }

  if (!displayedLogs) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-log-viewer text-log-viewer-muted text-sm p-6">
        {isRunning ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin mb-2 text-info-foreground" />
            <p className="text-info-foreground">Waiting for logs...</p>
          </>
        ) : (
          <>
            <p>No logs available.</p>
            {node.errorMessage && (
              <p className="text-xs mt-3 text-danger-foreground max-w-md text-center">{node.errorMessage}</p>
            )}
          </>
        )}
      </div>
    )
  }

  const filteredLogs = filterLogs(displayedLogs, logFilter)

  return (
    <div className="h-full flex flex-col bg-log-viewer">
      <div className="flex items-center gap-2 px-3 pt-3 pb-2 shrink-0">
        <div className="inline-flex items-center gap-1 rounded-lg border border-log-filter-border bg-log-filter p-1">
          {(['all', 'system', 'errors'] as LogFilter[]).map(f => (
            <button
              key={f}
              onClick={() => setLogFilter(f)}
              aria-pressed={logFilter === f}
              className={cn(
                'px-5 py-1 rounded-md text-xs font-medium transition-colors',
                logFilter === f
                  ? 'bg-log-filter-active text-log-filter-active-foreground'
                  : 'text-log-filter-foreground hover:bg-log-filter-active/15'
              )}
            >
              {f === 'all' ? 'All' : f === 'system' ? 'System' : 'Errors'}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {canLoadFullLogs && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 border-log-filter-border bg-log-filter text-log-filter-foreground hover:bg-log-filter-active hover:text-log-filter-active-foreground"
              disabled={persisted.isFetching}
              onClick={() => (loadPersisted ? persisted.refetch() : setLoadPersisted(true))}
              aria-label="Load full logs"
              data-testid="load-full-logs-button"
            >
              {persisted.isFetching ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <FileText className="h-3 w-3 mr-1" />
              )}
              Load full logs
            </Button>
          )}
          <CopyButton
            text={displayedLogs}
            tooltipLabel="Copy logs"
            className="bg-log-filter hover:bg-log-filter-active [&_svg]:text-log-filter-foreground hover:[&_svg]:text-log-filter-active-foreground"
          />
        </div>
      </div>
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden [scrollbar-color:var(--log-scrollbar-thumb)_transparent] hover:[scrollbar-color:var(--log-scrollbar-thumb-hover)_transparent] px-4 pb-4 pt-1 font-mono text-xs leading-relaxed text-log-viewer-foreground whitespace-pre-wrap break-words"
      >
        {filteredLogs || (logFilter !== 'all' && (
          <p className="text-log-viewer-muted">No {logFilter} log lines found.</p>
        ))}
        {isRunning && logFilter === 'all' && (
          <span className="inline-block w-2 h-4 bg-log-viewer-foreground animate-pulse ml-0.5" />
        )}
        {liveLogsPaused && (
          <p role="status" className="mt-3 text-warning-foreground">
            Live output paused: container logs are temporarily unavailable. Showing the last output received; it resumes automatically.
          </p>
        )}
        {persisted.isError && (
          <p role="alert" className="mt-3 text-danger-foreground">Failed to load full logs.</p>
        )}
        {noPersistedLogs && (
          <p className="mt-3 text-log-viewer-muted">No persisted logs were stored for this node.</p>
        )}
      </div>
    </div>
  )
}
